import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getStorageToken, useLogout } from "../../utilities";
import { LoadingSpinner, ModalMessage } from "../../components";
import * as Yup from "yup";
import { Container, Row } from "react-bootstrap";
import { provinces } from "../../constants/provincesAndStates";
import { useNavigate } from "react-router-dom";
import { updateAppConfigApi } from "../../api/appConfigApi";
import { loadAppConfig } from "../../redux/appConfigSlice";
import {
  deleteCloudinaryImagesApi,
  deleteCloudinaryVideosApi,
  uploadLargeCloudinaryImagesApi,
  uploadLargeCloudinaryVideosApi,
  uploadLogoCloudinaryImagesApi
} from "../../api/cloudinaryApi";
import { successMsgDuration } from "../../constants/config";
import SettingsForm from "../../components/admin/SettingsForm";


export default function AdminSettings() {
  // displays settings form where user can set basic configuration settings like store name, contact info etc
  // user can set images for the homepage (footer image, top image, top video)

// form validation schema (with Yup)
const validationSchema = Yup.object().shape({
  storeName: Yup.string().required("Please enter a store name."),
  phone: Yup.string()
    .matches(
      /^\+?\d{10,14}$/, //regex phone nr
      "Please enter a valid phone number (numbers only)."
    )
    .required("Please enter a phone number."),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter your email"),
  street: Yup.string().required("Please enter a street."),
  city: Yup.string().required("Please enter a city."),
  province: Yup.string().required("Please enter a province."),
  postalCode: Yup.string()
    .matches(
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, //regex Canadian postal code
      "Please enter a valid postal code"
    )
    .required("Please enter a postal code."),
});

const formFields = [
  { name: "storeName", label: "Store Name", type: "text" },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "street", label: "Street", type: "text" },
  { name: "city", label: "City", type: "text" },
  {
    name: "province",
    label: "Province",
    type: "select",
    options: provinces,
  },
  { name: "postalCode", label: "Postal Code", type: "text" },
];

  const navigate = useNavigate();
  const { stayLoggedIn } = useSelector((state) => state.user);
  const settingsData = useSelector((state) => state.appConfig);
  const dispatch = useDispatch();
  const logout = useLogout();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
const[showAlertMsg, setShowAlertMsg] = useState(false);

  const handleConfirm = () => {
      navigate("/admin");
  };

  const deleteUploadedFiles = async (
    deletedImagesPublicIds,
    deletedVideosPublicIds
  ) => {
    try {
      const token = getStorageToken(stayLoggedIn);
      if (deletedImagesPublicIds && deletedImagesPublicIds.length > 0) {
        await deleteCloudinaryImagesApi(token, deletedImagesPublicIds);
      }
      if (deletedVideosPublicIds && deletedVideosPublicIds.length > 0) {
        await deleteCloudinaryVideosApi(token, deletedVideosPublicIds);
      }
    } catch (error) {
      setErrorMsg(
        "Files could not be deleted from the server. Please contact our support team for assistance."
      );
    }
  };

  const handleFileUpload = async (
    id,
    newTopVideo,
    newTopImage,
    newFooterImage,
    newLogo,
  ) => {
    //when creating new product/updating product, do image upload in background
    try {
      const token = getStorageToken(stayLoggedIn);
      const signal = new AbortController().signal;
      let uploadedFiles = {};
      if (newFooterImage) {
        const uploadedImages = await uploadLargeCloudinaryImagesApi(
          token,
          [newFooterImage],
          signal
        );
        uploadedFiles.footerImage = uploadedImages[0];
      }
      if (newTopImage) {
        const uploadedImages = await uploadLargeCloudinaryImagesApi(
          token,
          [newTopImage],
          signal
        );
        uploadedFiles.topImage = uploadedImages[0];
      }
      if (newTopVideo) {
        const uploadedVideos = await uploadLargeCloudinaryVideosApi(
          token,
          [newTopVideo],
          signal
        );
        uploadedFiles.topVideo = uploadedVideos[0];
      }
      if (newLogo) {
        const uploadedImages = await uploadLogoCloudinaryImagesApi(
          token,
          [newLogo],
          signal
        );
        uploadedFiles.logo = uploadedImages[0];
      }
      await updateAppConfigApi(id, uploadedFiles, token);
      dispatch(loadAppConfig());
    } catch (error) {
      setErrorMsg(
        "Image upload failed. Please try again later, or contact our support team for further assistance."
      );
    }
  };

  const handleSubmitClick = async (
    id,
    updatedData,
    deletedImagesPublicIds,
    deletedVideosPublicIds,
    newTopVideo,
    newTopImage,
    newFooterImage, 
    newLogo,
  ) => {
    try {
      setIsLoading(true);
      const token = getStorageToken(stayLoggedIn);
      const signal = new AbortController().signal;
      await updateAppConfigApi(id, updatedData, token, signal);
      deleteUploadedFiles(deletedImagesPublicIds, deletedVideosPublicIds);
      if (newTopVideo || newTopImage || newFooterImage || newLogo) {
        await handleFileUpload(id, newTopVideo, newTopImage, newFooterImage, newLogo);
      } else {
        dispatch(loadAppConfig());
      }
      setSuccessMsg(
        "Successfully updated the settings." +
          "\n" +
          "Please allow a few moments for the media files to be uploaded in the background."
      );
      setIsLoading(false);
      await new Promise((resolve) => setTimeout(resolve, successMsgDuration));
      navigate(`/admin`, { replace: true });
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg("Could not update data. Please try again later.");
    }
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <h1 className="mb-4">Settings</h1>
      {isLoading && <LoadingSpinner />}
      <Row>
        <SettingsForm
          validationSchema={validationSchema}
          settingsData={settingsData}
          handleSubmitClick={handleSubmitClick}
          handleCancel={() => setShowAlertMsg(true)}
          formFields={formFields}
        />
      </Row>
                  {/* alert message */}
                  {showAlertMsg && 
      <ModalMessage
          message="Your changes have not been saved, yet. Are you sure you want to leave this page?"
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirm}
          setShowAlertMsg={setShowAlertMsg}
        />
      }
    </Container>
  );
}
